import { IComment } from '_/common/types/comments.types';
import { IReadmeVersion } from '_/common/types/readme.types';
import { ISource, ISourceWithExtras } from '_/types/sources/source.types';
import zeusSdk from '_/utils/SDKs';
import { formatExtractorUrl } from '_/utils/urlUtils';

export const fetchCollectionSources = async (collectionId: string): Promise<ISourceWithExtras[]> => {
  try {
    const options = {
      queryParameters: {
        sort: 'name',
        sortDirection: 'ASC',
      },
      include: 'editor,assignedUser',
    };
    const response = await zeusSdk.collections.getAllChildren(collectionId, 'sources', options);
    const sources = Array.isArray(response) ? response : [];

    // Transform sources data to match the format expected by the component
    return sources.map((source: ISource) => ({
      ...source,
      sourceId: source.id,
      sourceStatus: source.status,
      statusText: source.statusText,
      labels: source.labels,
      extractor: source.extractorId
        ? {
            name: source.extractorId,
            href: formatExtractorUrl(source.extractorId),
          }
        : null,
      formattedParameters: Object.entries(source.parameters || {}).map(([key, value]) => `${key}=${value ? value : '""'}`),
    }));
  } catch (error) {
    console.error('Error fetching collection sources', error);
    return [];
  }
};

export const updateSource = async (sourceId: string, updates: Partial<ISource>): Promise<ISource> => {
  try {
    const updatedSource = await zeusSdk.sources.patch(sourceId, updates);
    return updatedSource;
  } catch (error) {
    console.error('Error updating source', error);
    throw error;
  }
};

export const updateSourcesParams = async (sources: any[], params: Record<string, any>): Promise<void> => {
  await Promise.all(
    sources.map((src) => {
      const updatedSource = { parameters: { ...src.parameters, ...params } };
      return zeusSdk.sources.patch(src.id, updatedSource);
    }),
  );
};

export const fetchSource = async (collectionId: string, sourceSlug: string): Promise<ISource> => {
  try {
    const source = await zeusSdk.collections.getChildById(collectionId, 'sources', sourceSlug, {
      include: 'editor,assignedUser,extractor,extractorVersion',
    });
    return source;
  } catch (error) {
    console.error('Error fetching source', error);
    throw error;
  }
};

export const fetchSourceReadme = async (sourceId: string): Promise<string> => {
  try {
    const sourceReadme = await zeusSdk.sources.getChild(sourceId, 'readme');
    return sourceReadme?.content || '';
  } catch (error) {
    if ((error as any).status !== 404) {
      console.error(`Error fetching source readme for ${sourceId}`, error);
    }
    return '';
  }
};

export const fetchSourceComments = async (sourceId: string): Promise<IComment[]> => {
  try {
    const response = await zeusSdk.sources.listComments(sourceId, { include: 'editor' });
    return response.items || [];
  } catch (error) {
    console.error('Error fetching source comments', error);
    return [];
  }
};

export const createSourceComment = async (sourceId: string, comment: string): Promise<any> => {
  try {
    const newComment = await zeusSdk.sources.postComment(sourceId, comment);
    return newComment;
  } catch (error) {
    console.error('Error creating source comment', error);
    throw error;
  }
};

export const fetchSourceStateTransitions = async (sourceId: string): Promise<any[]> => {
  try {
    const response = await zeusSdk.sources.getTransitions(sourceId);
    return response.items || [];
  } catch (error) {
    console.error(`Error fetching source state transitions for ${sourceId}`, error);
    return [];
  }
};

export const fetchSourceHistory = async (sourceId: string, cursor?: string): Promise<{ items: any[]; cursor: string | null }> => {
  try {
    const options = {
      include: 'editor',
      queryParameters: {
        limit: 50,
        sortDirection: 'DESC',
        ...(cursor && { cursor: cursor }),
      },
    };
    const response = await zeusSdk.sources.getChild(sourceId, '_versions', options);
    return {
      items: response.items || [],
      cursor: response.cursor || null,
    };
  } catch (error) {
    console.error('Error fetching source history', error);
    return { items: [], cursor: null };
  }
};

export const fetchSourceReadmeVersions = async (
  sourceId: string,
  cursor?: string,
): Promise<{ items: IReadmeVersion[]; cursor: string | null }> => {
  try {
    const options = {
      include: 'editor',
      queryParameters: {
        limit: 50,
        sortDirection: 'DESC',
        ...(cursor && { cursor: cursor }),
      },
    };
    const response = await zeusSdk.sources.getChild(sourceId, 'readme/_versions', options);
    return {
      items: response.items || [],
      cursor: response.cursor || null,
    };
  } catch (error) {
    console.error('Error fetching source readme versions', error);
    return { items: [], cursor: null };
  }
};

export const fetchHistoricalSourceMetrics = async (sourceId: string, checkId: string): Promise<Record<string, any[]>> => {
  try {
    const response = await zeusSdk.sources.getHistoricalSourceMetrics(sourceId, checkId);
    return response || {};
  } catch (error) {
    console.error('Error fetching historical source metrics', error);
    return {};
  }
};

export const createSource = async (collectionId: string, source: Partial<ISource>, sourceReadme: string): Promise<any> => {
  // default status to QUEUED for new sources
  const sourceData = {
    ...source,
    collectionId: collectionId,
    status: 'QUEUED',
    // @ts-ignore
    labels: Array.isArray(source.labels) ? source.labels : source.labels?.split(',').map((i: string) => i.trim()),
  };

  const newSource = await zeusSdk.sources.create(sourceData);
  await zeusSdk.sources.createChild(newSource.id, 'readme', { content: sourceReadme });

  return newSource;
};

export const updateSourceReadme = async (sourceId: string, content: string): Promise<any> => {
  return zeusSdk.sources.updateChild(sourceId, 'readme', { content: content });
};

export const runSource = async (sourceId: string): Promise<any> => {
  try {
    const snapshot = await zeusSdk.sources.start(sourceId);
    return snapshot;
  } catch (error) {
    console.error('Error running source', error);
    throw error;
  }
};

export const updateExtractorOwner = async (extractorId: string, body: any): Promise<void> => {
  try {
    await zeusSdk.legacyExtractors.updateExtractorOwner(extractorId, body);
  } catch (error) {
    console.error('Error updating extractor owner', error);
    throw error;
  }
};

export const migrateLatestRuntimeConfig = async (sourceId: string): Promise<void> => {
  try {
    await zeusSdk.sources.migrateExtractor(sourceId);
  } catch (error) {
    console.error('Error migrating latest runtime config', error);
    throw error;
  }
};
